import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useLocalStorage } from './useLocalStorage';
import { useOfflineStatus } from './useOfflineStatus';
import { BASE_CURRENCY, CACHE_DURATION } from '../lib/constants';

interface ExchangeRate {
  symbol: string;
  rate: number;
}

interface RatesCache {
  rates: Record<string, number>;
  fetchedAt: number;
}

export function useRates() {
  const [rates, setRates] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number>(0);
  const [cache, setCache] = useLocalStorage<RatesCache | null>('rates_cache', null);
  const { isOnline } = useOfflineStatus();

  const isStale = useCallback((timestamp: number) => {
    return Date.now() - timestamp > CACHE_DURATION;
  }, []);

  const fetchRates = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      // Use cache if available and not stale (unless force refresh)
      if (!forceRefresh && cache && !isStale(cache.fetchedAt)) {
        setRates(cache.rates);
        setLastFetched(cache.fetchedAt);
        setLoading(false);
        return;
      }

      // If offline, use cache regardless of staleness
      if (!isOnline) {
        if (cache) {
          setRates(cache.rates);
          setLastFetched(cache.fetchedAt);
          setError('Working offline with cached rates');
        } else {
          setError('No cached rates available offline');
        }
        setLoading(false);
        return;
      }

      // Fetch from Supabase
      const { data: ratesData, error: ratesError } = await supabase
        .from('exchange_rates')
        .select('symbol, rate')
        .eq('base', BASE_CURRENCY)
        .order('fetched_at', { ascending: false });

      if (ratesError) throw ratesError;

      // Convert to rates object
      const newRates: Record<string, number> = { [BASE_CURRENCY]: 1 };
      ratesData?.forEach((rate: ExchangeRate) => {
        newRates[rate.symbol] = rate.rate;
      });

      const fetchedAt = Date.now();
      const newCache = { rates: newRates, fetchedAt };

      setRates(newRates);
      setLastFetched(fetchedAt);
      setCache(newCache);
    } catch (err) {
      console.error('Error fetching rates:', err);
      setError(isOnline ? 'Failed to fetch exchange rates' : 'Working offline with cached rates');
      
      // Fall back to cache if available
      if (cache) {
        setRates(cache.rates);
        setLastFetched(cache.fetchedAt);
      }
    } finally {
      setLoading(false);
    }
  }, [cache, setCache, isStale, isOnline]);

  const convertAmount = useCallback((
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ): number => {
    if (fromCurrency === toCurrency) return amount;
    
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[toCurrency] || 1;
    
    // Convert to base currency first, then to target currency
    const baseAmount = amount / fromRate;
    const convertedAmount = baseAmount * toRate;
    const roundedToFourDecimals = Math.round((convertedAmount + Number.EPSILON) * 10000) / 10000;
    return roundedToFourDecimals;
  }, [rates]);

  const refreshRates = useCallback(() => {
    return fetchRates(true);
  }, [fetchRates]);

  // Initialize rates on mount
  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  return {
    rates,
    loading,
    error,
    lastFetched,
    isStale: isStale(lastFetched),
    isOffline: !isOnline,
    convertAmount,
    refreshRates,
  };
}